import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import styles from './dailyBatchHist.module.scss';

const STATE_LABELS = {
    Succeeded: { label: 'Succeeded', cls: styles.stateSucceeded },
    Failed:    { label: 'Failed',    cls: styles.stateFailed },
    Running:   { label: 'Running',   cls: styles.stateRunning },
    Pending:   { label: 'Pending',   cls: styles.statePending },
    Skipped:   { label: 'Skipped',   cls: styles.stateSkipped },
};

const RUN_STATE_LABELS = {
    Succeeded: { cls: styles.stateSucceeded },
    Failed:    { cls: styles.stateFailed },
    Running:   { cls: styles.stateRunning },
    Pending:   { cls: styles.statePending },
    Cancelled: { cls: styles.stateSkipped },
};

const StateBadge = ({ state }) => {
    const cfg = STATE_LABELS[state] || { label: state, cls: '' };
    return <span className={`${styles.stateBadge} ${cfg.cls}`}>{cfg.label}</span>;
};

const formatDuration = (seconds) => {
    if (seconds == null) return '—';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
};

const formatTime = (dt) => dt ? moment(dt).format('HH:mm:ss') : '—';

const DailyBatchRunDetail = ({ detail, loading, onBack }) => {
    if (loading && !detail) {
        return (
            <div className={styles.detail}>
                <button className={styles.backButton} onClick={onBack} type="button">← Back</button>
                <div className={styles.detailLoading}>Loading run status…</div>
            </div>
        );
    }

    if (!detail) return null;

    const runStatusCls = (RUN_STATE_LABELS[detail.overallStatus] || {}).cls || '';
    const progressPct = detail.totalJobs > 0
        ? Math.round((detail.completedJobs / detail.totalJobs) * 100)
        : 0;
    const sortedJobs = [...(detail.jobs || [])].sort((a, b) => a.sequenceOrder - b.sequenceOrder);

    return (
        <div className={styles.detail}>
            <button className={styles.backButton} onClick={onBack} type="button">← Back</button>

            <div className={styles.detailHeader}>
                <div className={styles.detailTitle}>
                    {moment(detail.businessDate).format('DD MMM YYYY')}
                    <span className={`${styles.stateBadge} ${runStatusCls} ${styles.detailStatusBadge}`}>
                        {detail.overallStatus}
                    </span>
                    {loading && <span className={styles.pollingIndicator}>↻</span>}
                </div>
                <div className={styles.detailMeta}>
                    <span>Started: {formatTime(detail.startTime)}</span>
                    <span>Duration: {formatDuration(detail.durationSeconds)}</span>
                    <span>Initiated by: {detail.initiatedBy}</span>
                    <span>SEC mode: {detail.secMode || '—'}</span>
                </div>
                {detail.errorMessage && (
                    <div className={styles.detailError}>{detail.errorMessage}</div>
                )}
            </div>

            <div className={styles.progressSection}>
                <div className={styles.progressLabel}>
                    {detail.completedJobs} / {detail.totalJobs} stages complete
                    {detail.failedJobs > 0 && <span className={styles.progressFailed}> · {detail.failedJobs} failed</span>}
                </div>
                <div className={styles.progressBar}>
                    <div
                        className={`${styles.progressFill} ${detail.failedJobs > 0 ? styles.progressFillFailed : ''}`}
                        style={{ width: `${progressPct}%` }}
                    />
                </div>
            </div>

            <table className={styles.jobTable}>
                <thead>
                    <tr>
                        <th>Stage</th>
                        <th>Job</th>
                        <th>State</th>
                        <th>Started</th>
                        <th>Duration</th>
                        <th>Error</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedJobs.map(job => (
                        <tr key={job.jobName} className={job.state === 'Running' ? styles.jobRowRunning : ''}>
                            <td className={styles.jobStage}>{job.stage}</td>
                            <td>{job.jobDisplayName || job.jobName}</td>
                            <td><StateBadge state={job.state} /></td>
                            <td>{formatTime(job.startTime)}</td>
                            <td>{formatDuration(job.durationSeconds)}</td>
                            <td className={styles.jobError}>{job.errorMessage || ''}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

DailyBatchRunDetail.propTypes = {
    detail: PropTypes.shape({
        businessDate: PropTypes.string,
        overallStatus: PropTypes.string,
        startTime: PropTypes.string,
        durationSeconds: PropTypes.number,
        initiatedBy: PropTypes.string,
        secMode: PropTypes.string,
        errorMessage: PropTypes.string,
        totalJobs: PropTypes.number,
        completedJobs: PropTypes.number,
        failedJobs: PropTypes.number,
        jobs: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    loading: PropTypes.bool,
    onBack: PropTypes.func.isRequired,
};

DailyBatchRunDetail.defaultProps = {
    detail: null,
    loading: false,
};

export default DailyBatchRunDetail;
